import axios from "axios";
import { debounce } from "lodash-es";
import * as auth from "./auth";
import getConfig from "next/config";
import { message } from "@/utils/toast";
import { useSessionWithRoles } from "../hooks/useSessionWithRoles";
import { trackApiCall } from "../config/analytics/apiTracking";

const { publicRuntimeConfig } = getConfig();

const instance: any = axios.create({
  timeout: 5 * 60 * 1000,
  baseURL: publicRuntimeConfig.AIR_LCL_COMMON_BFF_URL,
});

const reqFuncHandle = async (config: any) => {
  // Start the clock for the api.call analytics event; read again in both response interceptors.
  config.metadata = { ...(config.metadata ?? {}), startedAt: Date.now() };
  config.headers = config.headers ?? {};
  const accessToken: any = auth.getToken();
  config.headers.Authorization = `Bearer ${accessToken}`;
  if (
    config.baseURL == publicRuntimeConfig.AIR_LCL_MASTERDATA_TRIAGE_TOOL_URL ||
    config.baseURL == "http://localhost:3001"
  ) {
    config.headers["Content-Type"] = "application/json";

    config.headers.Authorization = `Basic ${publicRuntimeConfig.AIR_LCL_AUTH_CODE}`;
  }
  config.headers = config.headers ?? {};
  if (config.headers["x-ms-blob-type"]) {
    // only the blob upload request needs this header
    delete config.headers.Authorization;
  }
  const urlParams = new URLSearchParams(config.url?.slice(config.url?.indexOf("?") + 1));
  config.headers["x-tenant-id"] = urlParams.get("companyCode") ?? config.data?.companyCode;
  if (config.url.includes("assignToMe")) {
    const decodeToken = auth.decodeToken(accessToken);
    console.log("decodeToken1111", decodeToken);
    config.headers["x-user-email"] = decodeToken!.email;
    config.headers["x-user-id"] = decodeToken!.oid;
    config.headers["x-user-name"] = decodeToken!.name;
  }
  return config;
};
instance.interceptors.request.use(reqFuncHandle, (err: any) => Promise.reject(err));

const errors = {
  invalidParamters: debounce(() => console.warn("Invalid paramters"), 1000),
  unauthorized: debounce(() => console.warn("You are not authorized"), 1000),
  internalServerError: debounce(() => console.warn("Internal Server Error"), 1000),
  inProgressError: debounce(
    () => console.warn("conflict with the current state of the target resource"),
    1000
  ),
};
const responseHandle = (err: any) => {
  if (axios.isCancel(err) || err?.code === "ERR_CANCELED") {
    // A cancelled request is the caller changing its mind — the same class of non-event as a
    // cancelled route change. Recording it as a failure would repeat exactly the mistake that
    // made the first error panel useless.
    return Promise.reject(err);
  }
  trackApiCall(
    {
      url: err?.config?.url,
      method: err?.config?.method,
      status: err?.response?.status,
      startedAt: err?.config?.metadata?.startedAt,
      errorMessage: err?.response?.data?.errorMessage || err?.message,
    },
    "failure"
  );
  console.error("Error response", err);
  // Any status codes that falls outside the range of 2xx cause this function to trigger
  if (err.response?.status === 400) {
    if (err.response?.data?.errorMessage) {
      message.error(err.response.data.errorMessage);
    } else {
      errors.invalidParamters();
    }
  }
  if (err.response?.status === 401) {
    // window.location.href = "/login";
  }
  if (err.response?.status === 403) {
    if (err.response?.data?.errorMessage) {
      message.error(err.response.data.errorMessage);
    } else {
      errors.unauthorized();
    }
  }
  if (err.response?.status === 409) {
    if (err.response?.data?.errorMessage) {
      message.error(err.response.data.errorMessage);
    } else {
      errors.inProgressError();
    }
  }
  if (err.response?.status === 500) {
    errors.internalServerError();
  }
  if (err.response?.status === 424) {
    if (err.response?.data?.errorMessage) {
      message.error(err.response.data.errorMessage);
    }
  }

  return Promise.reject(err);
};
instance.interceptors.response.use((res: any) => {
  // Any status code that lie within the range of 2xx cause this function to trigger
  trackApiCall(
    {
      url: res?.config?.url,
      method: res?.config?.method,
      status: res?.status,
      startedAt: res?.config?.metadata?.startedAt,
    },
    "success"
  );
  return res;
}, responseHandle);

/**
 * Resolves `userUrl` against the axios instance's trusted baseURL and returns
 * a fully-qualified URL whose origin is guaranteed to match the baseURL.
 *
 * Using `new URL()` + origin validation is the CodeQL-recognised sanitisation
 * pattern for SSRF (CWE-918).  The returned URL is reconstructed from the
 * **trusted** origin so that even a crafted input cannot redirect the request.
 */
function buildSafeRequestUrl(userUrl: string, method: string): string {
  const baseURL: string = instance.defaults.baseURL;

  if (!baseURL) {
    throw new Error(`No baseURL configured for ${method} request`);
  }

  let resolved: URL;
  try {
    resolved = new URL(userUrl, baseURL);
  } catch {
    throw new Error(`Invalid URL in ${method}: malformed URL`);
  }

  const trusted = new URL(baseURL);

  if (resolved.origin !== trusted.origin) {
    throw new Error(
      `Invalid URL in ${method}: request to untrusted origin "${resolved.origin}" is not allowed`
    );
  }

  if (resolved.pathname.includes("..")) {
    throw new Error(`Invalid URL in ${method}: path traversal is not allowed`);
  }

  return trusted.origin + resolved.pathname + resolved.search;
}

async function post(url: string, data?: any) {
  const safeUrl = buildSafeRequestUrl(url, "post");
  let res = await instance.post(safeUrl, data);
  if (res.status === 200) {
    return res.data;
  }
}

async function upload(url: string, data?: FormData) {
  const safeUrl = buildSafeRequestUrl(url, "upload");
  const res = await instance.post(safeUrl, data, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  if (res.status === 200) {
    return res.data;
  }
}

async function get(url: string) {
  const safeUrl = buildSafeRequestUrl(url, "get");
  const res = await instance.get(safeUrl);

  if (res.status === 200) {
    return res.data;
  }
}

async function getWithSignal(url: string, signal: AbortSignal) {
  const safeUrl = buildSafeRequestUrl(url, "get");
  const res = await instance.get(safeUrl, { signal });

  if (res.status === 200) {
    return res.data;
  }
}

async function postWithSignal(url: string, data: any, signal: AbortSignal) {
  const safeUrl = buildSafeRequestUrl(url, "post");
  const res = await instance.post(safeUrl, data, { signal });

  if (res.status === 200) {
    return res.data;
  }
}

async function put(url: string, data?: any) {
  const safeUrl = buildSafeRequestUrl(url, "put");
  const res = await instance.put(safeUrl, data);
  if (res.status === 200) {
    return res.data;
  }
}

async function patch(url: string, data?: any) {
  const safeUrl = buildSafeRequestUrl(url, "patch");
  const res = await instance.patch(safeUrl, data);

  if (res.status === 200) {
    console.log("patch res", res);
    return res;
  }
}
async function delelte(url: string) {
  const safeUrl = buildSafeRequestUrl(url, "delete");
  const res = await instance.delete(safeUrl);

  if (res.status === 200) {
    return res;
  }
}
async function putUpload(url: string, data?: FormData) {
  const res = await instance.put(url, data, {
    headers: {
      "x-ms-blob-type": "BlockBlob",
      "Content-Type": "multipart/form-data",
    },
  });

  if (res.status === 200) {
    return res.data;
  }
}
function changeBaseUrl(rootURl: string | undefined) {
  if (rootURl) {
    instance.defaults.baseURL = rootURl;
  }
}
async function download(url: string, filename: string) {
  instance.defaults.responseType = "blob";
  const res = await instance.get(url);
  if (res.status === 200) {
    const blob = new Blob([res.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const encodedUri = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.style.visibility = "hidden";
    link.setAttribute("href", encodedUri);
    let fileName = filename || "";
    const disposition = res.headers["content-disposition"];
    if (disposition) {
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      const matches = filenameRegex.exec(disposition);
      if (matches != null && matches[1]) {
        fileName = matches[1];
        if (fileName.startsWith('"') && fileName.endsWith('"')) {
          fileName = fileName.substring(1, fileName.length - 1);
        }
      }
    }

    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
async function metaDataDownload(url: string, method: string, data?: any) {
  instance.defaults.responseType = "blob";
  instance.defaults.timeout = 1000 * 60 * 3; // 3 minutes
  let res;
  if (method === "get") {
    res = await instance.get(url);
  } else {
    res = await instance.post(url, data);
  }
  if (res.status === 200) {
    // Reset responseType to 'json'
    instance.defaults.responseType = "json";
    const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
    const encodedUri = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.style.visibility = "hidden";
    link.setAttribute("href", encodedUri);
    const disposition = res.headers["content-disposition"];
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = filenameRegex.exec(disposition);
    let filename = "file.xlsm";
    if (matches != null && matches[1]) {
      filename = matches[1];
      if (filename.startsWith('"') && filename.endsWith('"')) {
        filename = filename.substring(1, filename.length - 1);
      }
      console.log(filename); // users202404030231.xlsm
    }
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

async function getBlob(url: string): Promise<Blob> {
  const safeUrl = buildSafeRequestUrl(url, "getBlob");
  const prevResponseType = instance.defaults.responseType;
  instance.defaults.responseType = "blob";
  try {
    const res = await instance.get(safeUrl);
    if (res.status === 200) {
      return res.data as Blob;
    }
    throw new Error(`getBlob: unexpected status ${res.status}`);
  } finally {
    instance.defaults.responseType = prevResponseType;
  }
}

export {
  changeBaseUrl,
  post,
  postWithSignal,
  upload,
  get,
  getWithSignal,
  put,
  patch,
  delelte,
  download,
  metaDataDownload,
  putUpload,
  getBlob,
};
